import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { fetchDossierById } from "../../../lib/api/resources/dossiers";
import { ClaimDossierOverviewTab } from "./tabs/ClaimDossierOverviewTab";
import { ClaimDossierCalculationTab } from "./tabs/ClaimDossierCalculationTab";
import { ClaimDossierDocumentsTab } from "./tabs/ClaimDossierDocumentsTab";
import { ClaimDossierClaimInfoTab } from "./tabs/ClaimDossierClaimInfoTab";
import { fetchClaimsByDossierId } from "../../../lib/api/resources/claims";
import { ClaimDossierRecourseTab } from "./tabs/ClaimDossierRecourseTab";
import { ClaimDossierTasksTab } from "./tabs/ClaimDossierTasksTab";
import { ClaimDossierFinancialTab } from "./tabs/ClaimDossierFinancialTab";
import { ClaimDossierCommunicationTab } from "./tabs/ClaimDossierCommunicationTab";
import { ClaimDossierCorrespondenceTab } from "./tabs/ClaimDossierCorrespondenceTab";
import { Tabs, TabsProps } from "@/components/v2";

export const ClaimDossierDetail = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("overview");

  if (!id) return null;

  const { data, isLoading } = useQuery({
    queryKey: ["dossier", id],
    queryFn: () => fetchDossierById(id),
  });

  const claimQuery = useQuery({
    queryKey: ["claim", data?.id],
    enabled: !!data,
    queryFn: async () => {
      if (!data) return null;
      if (data) {
        const dossierId = data["id"];
        const claims = await fetchClaimsByDossierId(dossierId);
        const claim = claims.member?.length > 0 ? claims.member[0] : null;
        return claim;
      }
    },
  });

  const tabs: TabsProps["items"] = [
    {
      key: "overview",
      label: "Overzicht",
      children: (
        <ClaimDossierOverviewTab
          claim={claimQuery.data}
          data={data}
          isLoading={claimQuery.isLoading || isLoading}
          setActiveTab={setActiveTab}
        />
      ),
    },
    ...(claimQuery.data && claimQuery.data.status === "calculatie_goedgekeurd"
      ? [
          {
            key: "claiminfo",
            label: "Claim",
            children: <ClaimDossierClaimInfoTab />,
          },
        ]
      : []),
    ...(claimQuery.data &&
    claimQuery.data.status !== "concept" &&
    claimQuery.data.status !== "claim_verstuurd"
      ? [
          {
            key: "calculation",
            label: "Calculaties",
            children: (
              <ClaimDossierCalculationTab
                data={data}
                claim={claimQuery.data}
                setActiveTab={setActiveTab}
              />
            ),
          },
        ]
      : []),
    ...(data?.recourse?.accidentReport?.id
      ? [
          {
            key: "recourse",
            label: "Regres",
            children: <ClaimDossierRecourseTab data={data} />,
          },
        ]
      : []),
    {
      key: "documents",
      label: "Documenten",
      children: <ClaimDossierDocumentsTab />,
    },
    {
      key: "tasks",
      label: "Taken",
      children: <ClaimDossierTasksTab data={data} />,
    },
    {
      key: "financial",
      label: "Financieel",
      children: <ClaimDossierFinancialTab />,
    },
    {
      key: "communication",
      label: "Communicatie",
      children: <ClaimDossierCommunicationTab data={data} />,
    },
    {
      key: "correspondence",
      label: "Correspondentie",
      children: (
        <ClaimDossierCorrespondenceTab
          data={data}
          isLoading={claimQuery.isLoading || isLoading}
        />
      ),
    },
    {
      key: "logs",
      label: "Logboek",
      children: null,
    },
  ];

  useEffect(() => {
    const tabFromUrl = searchParams.get("activeTab");
    if (tabFromUrl) {
      const validTabKeys = tabs?.map((tab) => tab?.key).filter(Boolean);
      if (validTabKeys.includes(tabFromUrl)) {
        setActiveTab(tabFromUrl);
        searchParams.delete("activeTab");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [tabs]);

  return <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />;
};
